import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CodeMapCompilation } from "../../shared/codemap";
import type { ReviewGuide } from "../../shared/guide";
import { loadChewConfig, type FixtureDescriptor, type FixtureFile, type FixtureFiles } from "../lib/fixtures";
import {
  heartbeatHostedReviewClaim,
  HostedApiError,
  loadHostedPullContext,
  loadHostedSourceFile,
  previewHostedPull,
  releaseHostedReviewClaim,
  startHostedPullReview,
  type HostedBootstrap,
  type HostedPreview,
  type HostedReviewContext,
} from "../lib/hosted";
import { usePageMetadata } from "../hooks/usePageMetadata";
import { ReviewSkeleton } from "./LoadingSkeleton";
import { FailureState } from "./FailureState";
import { ReviewWorkspace } from "./ReviewPage";

function language(path: string): string {
  const extension = path.split(".").at(-1)?.toLowerCase();
  return ({
    cs: "csharp", ts: "typescript", tsx: "tsx", js: "javascript", jsx: "jsx", json: "json",
    sql: "sql", yaml: "yaml", yml: "yaml", tf: "hcl", py: "python", go: "go", rs: "rust",
    css: "css", html: "html", md: "markdown", proto: "protobuf",
  } as Record<string, string>)[extension ?? ""] ?? "text";
}

function pathsForLayer(guide: ReviewGuide, layerIndex: number): string[] {
  const layer = guide.layers[layerIndex];
  if (!layer) return [];
  const regions = new Map(guide.codeMap.regions.map((region) => [region.id, region]));
  return [...new Set(layer.items.flatMap((item) => item.regionIds.map((id) => regions.get(id)?.path).filter(Boolean) as string[]))];
}

function descriptorFor(
  bootstrap: HostedPreview,
  repository: string,
  reviewSession?: HostedBootstrap["reviewSession"],
): FixtureDescriptor {
  const additions = bootstrap.compilation.regions.reduce((total, region) => total + (region.addedLoc ?? 0), 0);
  const deletions = bootstrap.compilation.regions.reduce((total, region) => total + (region.deletedLoc ?? 0), 0);
  return {
    id: `live-${repository.replace("/", "-")}-${bootstrap.pull.number}`,
    repository,
    pullNumber: bootstrap.pull.number,
    headSha: bootstrap.pull.headSha,
    title: bootstrap.pull.title,
    authorLogin: bootstrap.pull.author,
    url: bootstrap.pull.url,
    mode: "live",
    draft: bootstrap.pull.draft,
    reviewable: true,
    claim: reviewSession?.claim,
    chewfile: {
      status: bootstrap.chewfile.status,
      requiredReasons: [],
      effectiveChangedLoc: bootstrap.compilation.summary.changedLoc,
      excludedChangedLoc: 0,
    },
    scope: bootstrap.guide.scope,
    guidePath: "",
    filesPath: "",
    queue: {
      estimatedMinutes: bootstrap.guide.queue.estimatedMinutes,
      difficulty: bootstrap.guide.queue.difficulty,
      areas: bootstrap.guide.queue.areas,
      score: 0,
      reason: bootstrap.guide.queue.priorityReason,
      reviewGap: bootstrap.guide.reviewRequirements.map((requirement) => requirement.kind).join(", "),
    },
    stats: { additions, deletions, changedFiles: bootstrap.guide.coverage.expectedFiles, commits: bootstrap.pull.commits },
  };
}

export function HostedReviewPage({ repository, pullNumber }: { repository: string; pullNumber: number }) {
  const [bootstrap, setBootstrap] = useState<HostedPreview>();
  const [reviewSession, setReviewSession] = useState<HostedBootstrap["reviewSession"]>();
  const [reviewContext, setReviewContext] = useState<HostedReviewContext>();
  const [connectionError, setConnectionError] = useState<string>();
  const [files, setFiles] = useState<FixtureFiles>();
  const filesRef = useRef<FixtureFiles | undefined>(undefined);
  const inFlight = useRef(new Set<string>());
  const [config, setConfig] = useState<Awaited<ReturnType<typeof loadChewConfig>>>();
  const [error, setError] = useState<unknown>();
  const [authenticationRequired, setAuthenticationRequired] = useState(false);
  const [claimLost, setClaimLost] = useState(false);

  usePageMetadata(
    bootstrap
      ? `#${pullNumber} ${bootstrap.pull.title} · CHEW`
      : `#${pullNumber} · ${repository} · CHEW`,
    bootstrap
      ? `Review ${repository}#${pullNumber}: ${bootstrap.pull.title}`
      : `Open ${repository}#${pullNumber} in CHEW.`,
  );

  const loadPaths = useCallback(async (paths: string[]) => {
    if (!bootstrap) return;
    const wanted = paths.filter((path) => !inFlight.current.has(path) && filesRef.current?.files[path]?.loading);
    if (wanted.length === 0) return;
    for (const path of wanted) inFlight.current.add(path);
    const loadOne = async (path: string) => {
      try {
        const loaded = await loadHostedSourceFile(repository, pullNumber, bootstrap.guide.source.headSha, path);
        const file: FixtureFile = {
          base: loaded.base,
          head: loaded.head,
          binary: loaded.binary,
          encoding: loaded.encoding,
          oldPath: loaded.oldPath,
          newPath: loaded.path,
          headBlobSha: loaded.headBlobSha,
          language: language(path),
          provenance: "github",
          loading: false,
        };
        setFiles((current) => {
          if (!current) return current;
          const next = { ...current, files: { ...current.files, [path]: file } };
          filesRef.current = next;
          return next;
        });
      } catch (reason) {
        const message = reason instanceof HostedApiError
          ? reason.message
          : "CHEW could not load this file from GitHub.";
        setFiles((current) => {
          if (!current) return current;
          const existing = current.files[path];
          if (!existing) return current;
          const next = {
            ...current,
            files: {
              ...current.files,
              [path]: { ...existing, loading: false, loadError: message },
            },
          };
          filesRef.current = next;
          return next;
        });
      } finally {
        inFlight.current.delete(path);
      }
    };
    const queue = [...wanted];
    await Promise.all(Array.from({ length: Math.min(4, queue.length) }, async () => {
      while (queue.length > 0) {
        const path = queue.shift();
        if (path) await loadOne(path);
      }
    }));
  }, [bootstrap, pullNumber, repository]);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([previewHostedPull(repository, pullNumber), loadChewConfig()])
      .then(async ([loadedBootstrap, loadedConfig]) => {
        if (cancelled) return;
        if (
          loadedBootstrap.compilation.sourceKey !== `${loadedBootstrap.guide.source.baseSha}..${loadedBootstrap.guide.source.headSha}` ||
          loadedBootstrap.compilation.diagnostics.some((diagnostic) => diagnostic.severity === "error")
        ) {
          throw new Error("The hosted guide did not pass its deterministic compilation gate.");
        }
        const placeholders = Object.fromEntries(loadedBootstrap.guide.coverage.files.map(({ path }) => [path, {
          base: "",
          head: "",
          language: language(path),
          provenance: "github",
          loading: true,
        } satisfies FixtureFile]));
        const bundle: FixtureFiles = {
          schemaVersion: 2,
          source: loadedBootstrap.guide.source,
          notice: "Fetched from immutable GitHub commits on demand.",
          files: placeholders,
        };
        filesRef.current = bundle;
        setBootstrap(loadedBootstrap);
        setConfig(loadedConfig);
        setFiles(bundle);
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        if (reason instanceof HostedApiError && reason.status === 401) setAuthenticationRequired(true);
        else setError(reason);
      });
    return () => { cancelled = true; };
  }, [pullNumber, repository]);

  useEffect(() => {
    if (!bootstrap) return;
    let cancelled = false;
    void startHostedPullReview(repository, pullNumber, bootstrap.pull.headSha)
      .then(({ headSha, reviewSession: started }) => {
        if (!cancelled && headSha === bootstrap.pull.headSha) setReviewSession(started);
      })
      .catch((reason: unknown) => {
        if (!cancelled) setConnectionError(reason instanceof Error ? reason.message : "Could not start the hosted review session.");
      });
    return () => { cancelled = true; };
  }, [bootstrap?.pull.headSha, pullNumber, repository]);

  useEffect(() => {
    if (!bootstrap) return;
    let cancelled = false;
    void loadHostedPullContext(repository, pullNumber)
      .then((loaded) => {
        if (!cancelled && loaded.headSha === bootstrap.pull.headSha) setReviewContext(loaded);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [bootstrap?.pull.headSha, pullNumber, repository]);

  useEffect(() => {
    if (!bootstrap || !files) return;
    const current = pathsForLayer(bootstrap.guide, 0);
    const next = pathsForLayer(bootstrap.guide, 1);
    void loadPaths(current);
    void loadPaths(next);
  }, [bootstrap, files === undefined, loadPaths]);

  useEffect(() => {
    if (!bootstrap || !reviewSession?.claim || reviewSession.claim.status !== "owned") return;
    let lastActivity = Date.now();
    let stopped = false;
    const recordActivity = () => { lastActivity = Date.now(); };
    const heartbeat = async () => {
      if (stopped || document.visibilityState !== "visible" || Date.now() - lastActivity > 5 * 60_000) return;
      try {
        if (await heartbeatHostedReviewClaim(reviewSession.id) === "lost") setClaimLost(true);
      } catch {
        // A temporary outage must not interrupt a local-first review.
      }
    };
    const release = () => { void releaseHostedReviewClaim(reviewSession.id).catch(() => undefined); };
    window.addEventListener("keydown", recordActivity, true);
    window.addEventListener("pointerdown", recordActivity, true);
    window.addEventListener("scroll", recordActivity, true);
    window.addEventListener("pagehide", release);
    const timer = window.setInterval(() => void heartbeat(), 60_000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      window.removeEventListener("keydown", recordActivity, true);
      window.removeEventListener("pointerdown", recordActivity, true);
      window.removeEventListener("scroll", recordActivity, true);
      window.removeEventListener("pagehide", release);
      window.setTimeout(() => {
        if (!window.location.pathname.startsWith("/live/")) release();
      }, 0);
    };
  }, [bootstrap, reviewSession]);

  const descriptor = useMemo(
    () => bootstrap ? descriptorFor(bootstrap, repository, reviewSession) : undefined,
    [bootstrap, repository, reviewSession],
  );
  const needPaths = useCallback((paths: string[]) => void loadPaths(paths), [loadPaths]);
  const warnings = useMemo(() => bootstrap ? [
    ...bootstrap.warnings,
    ...(reviewSession?.claim?.status === "held" ? [{
      code: "review_claim_held",
      message: `@${reviewSession.claim.reviewerLogin} is already reviewing this slot. You can still review it.`,
    }] : []),
    ...(connectionError ? [{ code: "review_connection_failed", message: connectionError }] : []),
    ...(claimLost ? [{
      code: "review_claim_lost",
      message: "The coordination claim expired or moved to another reviewer. Your review and drafts are still safe.",
    }] : []),
  ] : [], [bootstrap, claimLost, connectionError, reviewSession]);

  if (authenticationRequired) {
    const returnTo = window.location.pathname;
    return <div className="fatal-state hosted-sign-in"><strong>Sign in to open this live review.</strong><a className="primary-button" href={`/auth/github?returnTo=${encodeURIComponent(returnTo)}`}>Continue with GitHub</a></div>;
  }
  if (error) {
    return (
      <FailureState
        title="CHEW could not open this review."
        error={error}
        fallback="The live pull request could not be loaded."
        backHref="/"
      />
    );
  }
  if (!bootstrap || !config || !files || !descriptor) {
    return <ReviewSkeleton pullNumber={pullNumber} repository={repository} />;
  }
  return (
    <ReviewWorkspace
      compilation={bootstrap.compilation as CodeMapCompilation}
      config={config}
      descriptor={descriptor}
      files={files}
      guide={bootstrap.guide}
      hostedConnectionPending={!reviewSession && !connectionError}
      hostedSessionId={reviewSession?.id}
      onNeedPaths={needPaths}
      pullContext={reviewContext}
      pullDetails={bootstrap.pull}
      source="github"
      warnings={warnings}
      reviewAccess={bootstrap.reviewAccess}
      reviewKind={bootstrap.chewfile.status}
    />
  );
}
